// One member's sheet (memberships.get), over Customers › Memberships (MembershipsPage.tsx): its status,
// term, the visits used and the next one due, what was paid and earned, how it renews and bills; and,
// for those who may, its billing, auto-renew, the card it renews to, the card link, and suspend, resume
// or cancel (BL-009, BL-056, F-27). One from the import source says so while the company mirrors it, and
// offers none of those (history fix 2). On ui-web's dialog layer (Layer, DialogFrame), at the size the
// old dialog had, drawn with catalogued parts only (BL-189): Label, KeyValue, Notice, SelectField,
// Button and Link; Riverstone keeps Memberships.tsx's own.
import { useEffect, useState } from 'react';
import {
  Button,
  DialogFrame,
  Figures,
  KeyValue,
  Label,
  Layer,
  money,
  SelectField,
} from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText } from '../books/ui.tsx';
import type { Card } from '../history/cards-model.ts';
import { newId } from '../shared/ids.ts';
import { useMirror } from '../shared/mirror.ts';
import { AutoRenew } from './AutoRenew.tsx';
import { BillingActions, BillingValue } from './Billing.tsx';
import { MemberStatusActions, StatusSaid } from './MemberStatus.tsx';
import {
  labelKind,
  RENEWAL_HINT,
  RENEWAL_WORDS,
  renewalValue,
  statusWords,
  suspendNote,
  termSpanText,
  visitsText,
  whenText,
  type Member,
  type MembershipDetail,
} from './model.ts';
import './memberships.css';

/** A member's status as its word (Active, Cancels on Dec 1, Suspended). */
export function StatusWord({ m }: { m: Pick<Member, 'status' | 'cancelsOn'> }) {
  const [word, tone] = statusWords(m, dayText);
  return <Label kind={labelKind(tone)}>{word}</Label>;
}

/** Where this term's renewal stands, when one has started (BL-009), with the server's note. */
function RenewalSaid({ m }: { m: Pick<Member, 'renewal'> }) {
  if (!m.renewal) return null;
  const [word, tone] = RENEWAL_WORDS[m.renewal.state];
  const hint = RENEWAL_HINT[m.renewal.state];
  return (
    <>
      <Label kind={labelKind(tone)}>{word}</Label>
      {m.renewal.note && <span className="bp-member-quiet bp-member-block">{m.renewal.note}</span>}
      {hint && <span className="bp-member-quiet bp-member-block">{hint}</span>}
    </>
  );
}

export function MemberDetail({
  member: m,
  today,
  canChooseCard,
  canRenew,
  canManage = false,
  onChanged,
  onClose,
}: {
  member: Member;
  today: string;
  canChooseCard: boolean;
  /** memberships.sell: turn auto-renew on (with consent) or off. */
  canRenew: boolean;
  /** memberships.manage: suspend, resume and cancel it (BL-056). */
  canManage?: boolean;
  /** After its status changed, for the list it opened from. */
  onChanged?: () => void;
  onClose: () => void;
}) {
  const [d, setD] = useState<MembershipDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<{ message: string; customerId?: string; creditCents?: number } | null>(
    null,
  );
  const [version, setVersion] = useState(0);
  // the import source's while the company mirrors it: suspended, cancelled and renewed there (history fix 2)
  const theirs = useMirror() && !!d?.imported;
  useEffect(() => {
    client
      .call<MembershipDetail>('memberships.get', { id: m.id })
      .then(setD)
      .catch((e: unknown) => setError(errorText(e, 'Could not load this membership.')));
  }, [m.id, version]);
  const name = `${m.customerName}’s ${m.planName} membership`;
  const title = `${m.customerName}: ${m.planName}`;
  return (
    <Layer level="dialog" size="md" label={title} onClose={onClose}>
      <DialogFrame title={title} error={error} footer={<Button onPress={onClose}>Close</Button>}>
        <div className="bp-member-sheet">
          <p className="bp-member-quiet">{m.address}</p>
          {!d && !error && <p className="bp-member-quiet">Loading the membership…</p>}
          {d && (
            <>
              <p className="bp-member-line">
                <StatusWord m={{ status: d.status as Member['status'], cancelsOn: d.cancelsOn ?? null }} />
                {d.status === 'active' && !d.cancelsOn && (
                  <span className="bp-member-quiet"> Renews {whenText(d.endsOn, today)}</span>
                )}
                {theirs && <span className="bp-member-quiet"> From ServiceTitan</span>}
              </p>
              <StatusSaid said={said} />
              {/* why it is paused or ends (BL-056) */}
              {d.status === 'suspended' && (
                <p className="bp-member-line">
                  Suspended since {dayText(d.suspendedOn!)}
                  {d.suspendReason ? `: ${d.suspendReason}` : ''}. No renewal and no member prices until it is
                  resumed. <span className="bp-member-quiet">{suspendNote(d.endsOn, dayText)}</span>
                </p>
              )}
              {(d.cancelsOn || d.cancelledOn) && (
                <p className="bp-member-line">
                  {d.cancelsOn
                    ? `Cancels on ${dayText(d.cancelsOn)}`
                    : `Cancelled on ${dayText(d.cancelledOn!)}`}
                  {d.cancelReason ? `: ${d.cancelReason}` : ''}.
                  {d.refundedCents ? ` ${money(d.refundedCents)} of it was refunded as a credit.` : null}
                </p>
              )}
              <Figures
                label="The visits"
                items={[
                  { label: 'Visits', value: visitsText(d.visitsUsed, d.visitsTotal) },
                  { label: 'Next visit due', value: d.nextDueOn ? dayText(d.nextDueOn) : 'None' },
                ]}
              />
              <KeyValue
                label="What it’s worth and how it renews"
                items={[
                  // a span of two dates: a row, as a figure it wraps to four lines in the sheet
                  { label: 'Term', value: termSpanText(d.startsOn, d.endsOn) },
                  { label: 'Paid for the term', value: money(d.deferredCents) },
                  {
                    label: 'Earned so far',
                    value: money(d.recognisedCents),
                    sub: 'recognised visit by visit',
                  },
                  {
                    label: 'Renewal',
                    value: (
                      <>
                        {m.renewal && m.renewal.state !== 'scheduled' ? (
                          <RenewalSaid m={m} />
                        ) : d.billing ? (
                          // billed by instalments: its term isn't charged at once, so it's renewed by hand (BL-009)
                          'Renewed by hand when the term ends'
                        ) : (
                          renewalValue(d.renewal, m.autoRenew)
                        )}
                        {/* what's left to do for it (F-27): "Auto-renew on: add a card before Nov 5, 2027." */}
                        {d.renewal?.notice && <span> {d.renewal.notice}</span>}
                      </>
                    ),
                  },
                  // billed by instalments (BL-009): "Bills $20.00 monthly, next on Dec 3, 2026."
                  ...(d.billing ? [{ label: 'Billing', value: <BillingValue billing={d.billing} /> }] : []),
                ]}
              />
              {canManage && !theirs && d.billing && (d.status === 'active' || d.status === 'suspended') && (
                <BillingActions
                  billing={d.billing}
                  membershipId={d.id}
                  name={name}
                  today={today}
                  onChanged={(next, billing) => {
                    setSaid(next);
                    setD({ ...d, billing });
                  }}
                />
              )}
              {/* on with the customer's consent to the terms shown, or off (memberships.setAutoRenew, F-27):
                  after the facts, so the Renewal value reads on its own */}
              {canRenew && !theirs && d.status === 'active' && (
                <AutoRenew detail={d} membership={name} onChanged={(next) => setD({ ...d, renewal: next })} />
              )}
              {canChooseCard && !theirs && d.renewal?.on && (
                <RenewalCard detail={d} onChanged={(next) => setD({ ...d, renewal: next })} />
              )}
              {/* the customer adds the card (and their yes to the terms) themselves, from a link */}
              {canChooseCard && !theirs && d.status === 'active' && <SendCardLink membershipId={d.id} />}
              {canManage && !theirs && (
                <MemberStatusActions
                  key={`${d.status}-${d.cancelsOn ?? ''}`}
                  detail={d}
                  name={name}
                  today={today}
                  onChanged={(next) => {
                    setSaid(next);
                    setVersion((v) => v + 1);
                    onChanged?.();
                  }}
                />
              )}
            </>
          )}
        </div>
      </DialogFrame>
    </Layer>
  );
}

/**
 * "Send card link" (memberships.sendCardLink, BL-009 phase 4): texts or emails the customer a link to
 * add the card their membership renews to, agreeing to the renewal terms there if they haven't.
 */
function SendCardLink({ membershipId }: { membershipId: string }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);
  const send = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const r = await client.call<{ sent: boolean; reason: string | null }>(
        'memberships.sendCardLink',
        { membershipId },
        { idempotencyKey: newId() },
      );
      setSaid(
        r.sent
          ? { ok: true, text: 'Sent. The link works for 30 days, or until they save a card with it.' }
          : { ok: false, text: r.reason ?? 'The link wasn’t sent.' },
      );
    } catch (e) {
      setSaid({ ok: false, text: errorText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bp-member-renew">
      <p className="bp-member-line">
        <Button size="sm" isBusy={busy} onPress={() => void send()}>
          {busy ? 'Sending…' : 'Send card link'}
        </Button>{' '}
        <span className="bp-member-quiet">
          They add the card it renews to themselves; nothing is charged now.
        </span>
      </p>
      {said && (
        <p className={said.ok ? 'bp-member-line' : 'bp-member-bad'} role={said.ok ? 'status' : 'alert'}>
          {said.text}
        </p>
      )}
    </div>
  );
}

/** "Renews to [card]" (memberships.setRenewalCard): one of the customer's unexpired cards on file. */
function RenewalCard({
  detail: d,
  onChanged,
}: {
  detail: MembershipDetail;
  onChanged: (renewal: NonNullable<MembershipDetail['renewal']>) => void;
}) {
  const [cards, setCards] = useState<Card[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client
      .call<{ cards: Card[] }>('cards.list', { customerId: d.customerId })
      .then((r) => setCards(r.cards.filter((c) => !c.expired)))
      .catch((e: unknown) => setError(errorText(e, 'Could not read their cards on file.')));
  }, [d.customerId]);
  const current = d.renewal?.card?.id ?? '';
  const choose = async (cardId: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ renewal: NonNullable<MembershipDetail['renewal']> }>(
        'memberships.setRenewalCard',
        { membershipId: d.id, cardId },
      );
      onChanged(r.renewal);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (!cards) return error ? <p className="bp-member-bad">{error}</p> : null;
  return (
    <div className="bp-member-renew">
      {cards.length ? (
        <SelectField
          label="Renews to"
          value={current}
          isDisabled={busy}
          onChange={(v) => v && void choose(v)}
          options={[
            ...(current ? [] : [{ value: '', label: 'Choose a card on file' }]),
            ...cards.map((c) => ({ value: c.id, label: c.label })),
          ]}
        />
      ) : (
        <p className="bp-member-quiet">No card on their file yet: save one on their Money tab.</p>
      )}
      {error && (
        <p className="bp-member-bad" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
